import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
import { categories, products } from '../data/homeContent';
import { company } from '../data/company';
export function Home() {
  const {
    locale
  } = useLocale();
  const t = locale === 'nl' ? {
    since: `Sinds ${company.foundedYear}`,
    headline: 'Een hele winkel, op het ritme van uw dag.',
    lead: 'Verse markt, kruidenierswaren, huis en elektro in onze hypermarkt — plus een groothandelsteam voor wie in volume koopt. Alles op één adres in Brugge.',
    primary: 'Ontdek de afdelingen',
    secondary: 'Informatie aanvragen',
    caption: 'De gangen van onze winkel in Brugge',
    facts: [[`${company.yearsOfService}`, 'jaar in dienst van de regio'], ['85 000+', 'producten in het assortiment'], ['15', 'afdelingen onder één dak'], ['B2B', 'offerte op aanvraag']],
    indexLabel: 'Afdelingenindex',
    indexTitle: 'Uw boodschappen beginnen hier.',
    indexLink: 'Volledig winkelplan',
    storyLabel: 'De winkel',
    storyTitle: 'Een echte hypermarkt, gemaakt voor het echte leven.',
    storyBody: 'Vul uw kar, wandel door lichte gangen en vind verse voeding, huis, school, gezondheid en duizenden basisproducten in één bezoek. Onze afdelingen zijn zo geordend dat een grote boodschappenlijst een korte rit blijft.',
    storyPoints: [['Verse markt', 'Fruit, groenten, bakkerij, vlees en vis'], ['Elke dag', 'Huis, school, gezondheid, doe-het-zelf en elektro']],
    storyLink: 'Ontdek het assortiment',
    selectionLabel: 'Selectie',
    selectionTitle: 'Een greep uit het assortiment.',
    selectionBody: 'Representatief voor onze afdelingen vers, kruidenierswaren, huis, elektro en zakelijke levering.',
    availability: 'Beschikbaarheid verschilt per winkel',
    request: 'Informatie aanvragen',
    businessLabel: 'Groothandel & distributie',
    businessTitle: 'De leverancier achter uw ambitie.',
    businessBody: 'Van een zelfstandig café tot nationale organisaties: onze specialisten bouwen een leveringsprogramma rond uw werking.',
    businessCta: 'Praat met ons zakelijke team',
    services: ['Grote volumes & containerorders', 'Levering aan bedrijven & kantoren', 'Hotels & restaurants', 'Scholen, ziekenhuizen & instellingen', 'Import & export', 'Distributie in heel België'],
    closingLabel: 'Over ons',
    closingTitle: 'Lokaal geworteld. Wereldwijd verbonden.',
    closingBody: `${company.name} combineert retailkennis, groothandelscapaciteit en betrouwbare distributie om kwaliteitsproducten eenvoudig bereikbaar te maken.`,
    closingYears: 'jaar producten en mensen verbinden',
    closingLink: 'Ontdek ons verhaal'
  } : {
    since: `Since ${company.foundedYear}`,
    headline: 'A whole store, built around everyday life.',
    lead: 'Fresh market, pantry, home and electricals in our hypermarket — plus a wholesale team for anyone buying in volume. All at one address in Bruges.',
    primary: 'Explore departments',
    secondary: 'Request information',
    caption: 'The aisles of our Bruges store',
    facts: [[`${company.yearsOfService}`, 'years serving the region'], ['85,000+', 'products in the range'], ['15', 'departments under one roof'], ['B2B', 'quotation on request']],
    indexLabel: 'Department index',
    indexTitle: 'Start your shop here.',
    indexLink: 'Full store directory',
    storyLabel: 'The store',
    storyTitle: 'A real hypermarket, made for real life.',
    storyBody: 'Fill your basket, browse bright aisles, and find fresh food, home, school, health and thousands of essentials in one visit. Our departments are ordered so a long shopping list stays a short trip.',
    storyPoints: [['Fresh market', 'Fruit, vegetables, bakery, meat and seafood'], ['Every day', 'Home, school, health, DIY and appliances']],
    storyLink: 'Browse the assortment',
    selectionLabel: 'Selection',
    selectionTitle: 'A closer look at the range.',
    selectionBody: 'Representative of our fresh, pantry, home, equipment and supply departments.',
    availability: 'Availability varies by store',
    request: 'Request information',
    businessLabel: 'Wholesale & distribution',
    businessTitle: 'The supply partner behind your ambition.',
    businessBody: 'From an independent café to national organisations, our specialists build a supply programme around your operation.',
    businessCta: 'Talk to our business team',
    services: ['Bulk & container orders', 'Corporate and office supply', 'Hotels & restaurants', 'Schools, hospitals & institutions', 'Import & export', 'Nationwide distribution'],
    closingLabel: 'About us',
    closingTitle: 'Locally grounded. Globally connected.',
    closingBody: `${company.name} combines retail expertise, wholesale capability and reliable distribution to make quality products easy to access.`,
    closingYears: 'years connecting products with people',
    closingLink: 'Discover our story'
  };
  return <SiteLayout data-id="element-154" data-mp="24vjr064vwou2">
      <main data-id="element-155" data-mp="202qmnjdbviii">
        {/* Hero */}
        <section className="border-b border-ink" data-id="element-156" data-mp="201p9988uajxm">
          <div className="mx-auto grid max-w-[1500px] lg:grid-cols-[1.05fr_0.95fr]" data-id="element-157" data-mp="200xd9wbbyk6t">
            <div className="flex flex-col justify-center px-5 py-16 lg:border-r lg:border-ink lg:px-10 lg:py-24" data-id="element-158" data-mp="3ubdu9f9rdlvm">
              <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-159" data-mp="28mflyhj1xsz">
                {t.since}
              </p>
              <h1 className="mt-8 max-w-[16ch] font-display text-[3.25rem] font-semibold leading-[0.94] tracking-[-0.03em] sm:text-6xl lg:text-[4.75rem]" data-id="element-160" data-mp="3pjcg1nt15l2y">
                {t.headline}
              </h1>
              <p className="mt-8 max-w-xl text-lg leading-8 text-ink/70" data-id="element-161" data-mp="3fly8yeszy8nb">
                {t.lead}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-162" data-mp="31n8buz66g8yy">
                <Link to="/categories" className="border border-ink bg-ink px-6 py-4 font-mono text-[11px] uppercase tracking-[0.16em] text-paper transition hover:border-cobalt hover:bg-cobalt" data-id="element-163" data-mp="36k886id85xcv">
                  {t.primary}
                </Link>
                <Link to="/contact" className="group inline-flex items-center gap-2 border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.16em]" data-id="element-164" data-mp="1ewsdo5g9wanh">
                  {t.secondary}
                  <ArrowUpRightIcon size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-165" data-mp="1nrzzp8zriaoq" />
                </Link>
              </div>
            </div>
            <figure className="relative m-0" data-id="element-166" data-mp="1snx2sdm0lkjk">
              <img src="https://images.unsplash.com/photo-1601598851547-4302969d0614?auto=format&fit=crop&w=1600&q=95" alt={t.caption} className="h-full min-h-[380px] w-full object-cover lg:min-h-[620px]" data-id="element-167" data-mp="1sr2vs3xrl6kk" />
              <figcaption className="absolute bottom-0 left-0 bg-cobalt px-4 py-2 font-mono text-[10px] uppercase tracking-label text-chalk" data-id="element-168" data-mp="cbi3na02ua2m">
                {t.caption}
              </figcaption>
            </figure>
          </div>
        </section>

        {/* Facts strip */}
        <section className="border-b border-ink bg-ink text-paper" data-id="element-169" data-mp="11scyuzm9rkrd">
          <dl className="mx-auto grid max-w-[1500px] grid-cols-2 lg:grid-cols-4" data-id="element-170" data-mp="16juynkck3far">
            {t.facts.map(([value, label], index) => <div key={label} className={`border-paper/15 px-5 py-8 lg:px-10 ${index % 2 === 1 ? 'border-l' : ''} ${index < 2 ? 'border-b lg:border-b-0' : ''} ${index === 2 ? 'lg:border-l' : ''} ${index === 3 ? 'lg:border-l' : ''}`} data-id="element-171" data-mp="1akexcscs2qz0" data-mpi={index}>
                <dt className="font-display text-4xl leading-none" data-id="element-172" data-mp="mvm97i2edney">{value}</dt>
                <dd className="mt-3 font-mono text-[10px] uppercase leading-5 tracking-label text-paper/55" data-id="element-173" data-mp="ruqi07letj4r">
                  {label}
                </dd>
              </div>)}
          </dl>
        </section>

        {/* Department index list */}
        <section className="border-b border-ink" data-id="element-174" data-mp="238arbpayq4s3">
          <div className="mx-auto max-w-[1500px] px-5 py-16 lg:px-10" data-id="element-175" data-mp="2delshy3mlg0i">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-176" data-mp="2dfwupthfafpi">
              <div data-id="element-177" data-mp="28fr9ow5nrrge">
                <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-178" data-mp="28hbw1yhtk436">
                  {t.indexLabel}
                </p>
                <h2 className="mt-4 font-display text-4xl tracking-[-0.02em] sm:text-5xl" data-id="element-179" data-mp="1zlmek0jhrtv2">
                  {t.indexTitle}
                </h2>
              </div>
              <Link to="/categories" className="border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.16em] hover:text-cobalt hover:border-cobalt" data-id="element-180" data-mp="1pohq1u26bmb3">
                {t.indexLink}
              </Link>
            </div>
            <ul className="mt-12 border-t border-ink" data-id="element-181" data-mp="1gpwckdbk6xh6">
              {categories.map((category, index) => <li key={category.name.nl} className="border-b border-stone" data-id="element-182" data-mp="3f13a34m52z18" data-mpi={index}>
                  <Link to={category.to} className="group grid grid-cols-[3rem_1fr_auto] items-center gap-4 py-6 transition hover:bg-chalk sm:grid-cols-[4rem_1fr_1fr_auto] sm:gap-8" data-id="element-183" data-mp="3b2m7mydnycq5">
                    <span className="font-mono text-[11px] text-cobalt" data-id="element-184" data-mp="mnaskld97c0t">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-display text-2xl leading-tight tracking-[-0.01em] sm:text-3xl" data-id="element-185" data-mp="hlldmumwue7g">
                      {category.name[locale]}
                    </h3>
                    <p className="col-start-2 font-mono text-[10px] uppercase leading-5 tracking-label text-ink/45 sm:col-start-3" data-id="element-186" data-mp="3l5pvd8d1qct9">
                      {category.detail[locale]}
                    </p>
                    <ArrowUpRightIcon size={18} className="col-start-3 row-start-1 justify-self-end text-ink/40 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cobalt sm:col-start-4" data-id="element-187" data-mp="3v0zvtrnsqc4x" />
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Editorial store story */}
        <section className="border-b border-ink bg-chalk" data-id="element-188" data-mp="18v6gie3kbyg1">
          <div className="mx-auto grid max-w-[1500px] lg:grid-cols-[0.95fr_1.05fr]" data-id="element-189" data-mp="18wrbu5xpbley">
            <div className="grid grid-cols-2 border-b border-ink lg:border-b-0 lg:border-r" data-id="element-190" data-mp="1cxkykvzequ69">
              <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=90" alt="" loading="lazy" className="h-full w-full border-r border-ink object-cover" data-id="element-191" data-mp="20nfanbbwkepg" />
              <img src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=800&q=90" alt="" loading="lazy" className="h-full min-h-[300px] w-full object-cover" data-id="element-192" data-mp="1wk95k70j0tkk" />
            </div>
            <div className="px-5 py-16 lg:px-12 lg:py-20" data-id="element-193" data-mp="2muhzmp9hvfx3">
              <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-194" data-mp="2mtzt8zt4blbg">
                {t.storyLabel}
              </p>
              <h2 className="mt-6 max-w-[22ch] font-display text-4xl leading-[1.05] tracking-[-0.02em] sm:text-5xl" data-id="element-195" data-mp="2d4q2bna0g8ox">
                {t.storyTitle}
              </h2>
              <p className="mt-6 max-w-xl leading-8 text-ink/70" data-id="element-196" data-mp="2934jzmepj27c">
                {t.storyBody}
              </p>
              <dl className="mt-10 grid gap-px border-t border-stone sm:grid-cols-2" data-id="element-197" data-mp="241xaniwx1uyk">
                {t.storyPoints.map(([title, detail]) => <div key={title} className="border-b border-stone py-5" data-id="element-198" data-mp="1u75codzf9639">
                    <dt className="font-display text-xl" data-id="element-199" data-mp="1q4s9nxc27ma6">{title}</dt>
                    <dd className="mt-2 font-mono text-[10px] uppercase leading-5 tracking-label text-ink/45" data-id="element-200" data-mp="1q6lct2ngrodf">
                      {detail}
                    </dd>
                  </div>)}
              </dl>
              <Link to="/categories" className="mt-10 inline-block border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.16em] hover:border-cobalt hover:text-cobalt" data-id="element-201" data-mp="16b0jxf3vcwhf">
                {t.storyLink}
              </Link>
            </div>
          </div>
        </section>

        {/* Selection */}
        <section className="border-b border-ink" data-id="element-202" data-mp="1k1dtflwesj8v">
          <div className="mx-auto max-w-[1500px] px-5 py-16 lg:px-10" data-id="element-203" data-mp="ncfx3z90gd9l">
            <div className="max-w-2xl" data-id="element-204" data-mp="ne93rejmixhl">
              <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-205" data-mp="sc0xme3twtof">
                {t.selectionLabel}
              </p>
              <h2 className="mt-4 font-display text-4xl tracking-[-0.02em] sm:text-5xl" data-id="element-206" data-mp="wep7mp4a46mz">
                {t.selectionTitle}
              </h2>
              <p className="mt-5 leading-8 text-ink/70" data-id="element-207" data-mp="27s9iib9hd0cf">{t.selectionBody}</p>
            </div>
            <div className="mt-12 grid border-l border-t border-stone sm:grid-cols-2 lg:grid-cols-4" data-id="element-208" data-mp="2cqbbbnwpke13">
              {products.map(product => <article key={product.name.nl} className="border-b border-r border-stone bg-chalk" data-id="element-209" data-mp="2b9xjavchj6x3">
                  <div className="border-b border-stone bg-paper p-8" data-id="element-210" data-mp="2kavf4qcrs84l">
                    <img src={product.image} alt={`${product.name[locale]} — ${product.brand}`} loading="lazy" className="mx-auto h-40 w-full object-contain mix-blend-multiply" data-id="element-211" data-mp="2k80vv25hstcr" />
                  </div>
                  <div className="p-6" data-id="element-212" data-mp="33y34fj7gaxxg">
                    <p className="font-mono text-[10px] uppercase tracking-label text-cobalt" data-id="element-213" data-mp="33x1gwf990iyu">
                      {product.type[locale]}
                    </p>
                    <h3 className="mt-3 font-display text-xl leading-snug" data-id="element-214" data-mp="3cyrhaal3ys8r">
                      {product.name[locale]}
                    </h3>
                    <p className="mt-2 text-sm text-ink/55" data-id="element-215" data-mp="381j0oeflyw9m">
                      {product.brand} · {t.availability}
                    </p>
                    <Link to="/contact" className="mt-5 inline-block border-b border-ink/30 pb-0.5 font-mono text-[10px] uppercase tracking-label hover:border-cobalt hover:text-cobalt" data-id="element-216" data-mp="feypjwz9bk0">
                      {t.request}
                    </Link>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Business */}
        <section className="border-b border-ink bg-ink text-paper" data-id="element-217" data-mp="3dmqgyp15orn3">
          <div className="mx-auto grid max-w-[1500px] lg:grid-cols-2" data-id="element-218" data-mp="2yxkk37psjdid">
            <div className="px-5 py-16 lg:border-r lg:border-paper/15 lg:px-10 lg:py-24" data-id="element-219" data-mp="33w5cikyswsw9">
              <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-220" data-mp="33tsuy8wygkjq">
                {t.businessLabel}
              </p>
              <h2 className="mt-6 max-w-[20ch] font-display text-4xl leading-[1.04] tracking-[-0.02em] sm:text-5xl lg:text-[3.5rem]" data-id="element-221" data-mp="2uvqp9g2a13q4">
                {t.businessTitle}
              </h2>
              <p className="mt-6 max-w-xl leading-8 text-paper/65" data-id="element-222" data-mp="vo0g49p2rc9s">
                {t.businessBody}
              </p>
              <Link to="/wholesale" className="mt-10 inline-block border border-paper bg-paper px-6 py-4 font-mono text-[11px] uppercase tracking-[0.16em] text-ink transition hover:border-cobalt hover:bg-cobalt hover:text-paper" data-id="element-223" data-mp="qqh31d4455jf">
                {t.businessCta}
              </Link>
            </div>
            <ol className="px-5 py-10 lg:px-10 lg:py-16" data-id="element-224" data-mp="1ed752eggp041">
              {t.services.map((service, index) => <li key={service} className="flex items-baseline gap-6 border-b border-paper/15 py-5 last:border-0" data-id="element-225" data-mp="15ebxaxxrrhym" data-mpi={index}>
                  <span className="font-mono text-[11px] text-cobalt" data-id="element-226" data-mp="3v4yp994ml7ly">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="font-display text-xl sm:text-2xl" data-id="element-227" data-mp="25nbcmcxr7c8p">
                    {service}
                  </span>
                </li>)}
            </ol>
          </div>
        </section>

        {/* Closing */}
        <section data-id="element-228" data-mp="2elne5a05rufx">
          <div className="mx-auto grid max-w-[1500px] lg:grid-cols-[1fr_0.9fr]" data-id="element-229" data-mp="2el3thlj4iry7">
            <div className="px-5 py-16 lg:border-r lg:border-ink lg:px-10 lg:py-24" data-id="element-230" data-mp="2jf80g4se2zza">
              <p className="font-mono text-[11px] uppercase tracking-label text-cobalt" data-id="element-231" data-mp="2oc87ahx2c6rg">
                {t.closingLabel}
              </p>
              <h2 className="mt-6 max-w-[20ch] font-display text-4xl leading-[1.05] tracking-[-0.02em] sm:text-5xl" data-id="element-232" data-mp="2tdxh5u8juedy">
                {t.closingTitle}
              </h2>
              <p className="mt-6 max-w-xl leading-8 text-ink/70" data-id="element-233" data-mp="32c9ai40svqa3">
                {t.closingBody}
              </p>
              <div className="mt-10 flex items-end gap-5 border-t border-stone pt-8" data-id="element-234" data-mp="13z9a88iq9onb">
                <strong className="font-display text-6xl font-semibold leading-none" data-id="element-235" data-mp="17z1liszia8wd">
                  {company.yearsOfService}
                </strong>
                <span className="max-w-[180px] font-mono text-[10px] uppercase leading-5 tracking-label text-ink/50" data-id="element-236" data-mp="k3y3fxh24jrq">
                  {t.closingYears}
                </span>
              </div>
              <Link to="/about" className="mt-10 inline-block border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.16em] hover:border-cobalt hover:text-cobalt" data-id="element-237" data-mp="tspv04i7h224">
                {t.closingLink}
              </Link>
            </div>
            <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=90" alt={locale === 'nl' ? `Team van ${company.name}` : `${company.name} team`} loading="lazy" className="h-full min-h-[320px] w-full object-cover" data-id="element-238" data-mp="f43cxv171w77" />
          </div>
        </section>
      </main>
    </SiteLayout>;
}